import { useTranslation } from "react-i18next";
import { ClipboardCheck, ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { InspectionPointData } from "@/hooks/useTasksQuery";

interface InspectionPointCardProps {
  /** The assigned inspection-point (inspection_responses.assigned_to = current user), L6-3 / TES-1869. */
  point: InspectionPointData;
  /** Navigate to the parent inspection (the point is executed in its inspection context). */
  onOpenInspection?: (inspectionId: string) => void;
}

/**
 * "Meine Aufgaben" card for an assigned Prüfpunkt (inspection_responses.assigned_to).
 * Presentational only; navigation is delegated via onOpenInspection (parent wires it to the
 * inspection detail route, like the other task cards). The Prüfpunkt is worked inside its
 * parent inspection, so the primary action opens that inspection.
 */
export function InspectionPointCard({ point, onOpenInspection }: InspectionPointCardProps) {
  const { t } = useTranslation();
  const title = point.checklist_item_title || t("tasks.inspectionPoint");
  const status = point.inspection_status;

  return (
    <Card data-testid="inspection-point-card" className="transition-colors hover:bg-muted/40">
      <CardContent className="flex items-center gap-3 p-4">
        <ClipboardCheck className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="shrink-0">
              {t("tasks.inspectionPoint")}
            </Badge>
            {status && (
              <Badge variant="outline" className="shrink-0">
                {t(`history.inspectionStatus.${status}`, status)}
              </Badge>
            )}
          </div>
          <p className="mt-1 truncate font-medium" title={title}>
            {title}
          </p>
        </div>
        {onOpenInspection && (
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0"
            onClick={() => onOpenInspection(point.inspection_id)}
          >
            {t("tasks.openInspection")}
            <ChevronRight className="ml-1 h-4 w-4" aria-hidden="true" />
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
